import { useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';
import { AgentEventsEnum, SessionState } from '@heygen/liveavatar-web-sdk';

/**
 * useWebSocketControl Hook
 * 
 * Manages WebSocket control integration for the HeyGen avatar system.
 * Listens for speech commands and language changes from the backend Control Bridge,
 * and emits avatar state changes back to the backend.
 * 
 * @param {Object} sessionInfo - Current session information
 * @param {Object} aiFormData - AI form configuration data
 * @param {Object} repeatRef - Ref object containing the repeat function to trigger avatar speech
 * @param {Object} sessionRef - Reference to the HeyGen session
 * @param {boolean} isAvatarTalking - Whether the avatar is currently speaking
 * @param {Function} setDefaultLang - Function to update the language state in parent component
 * @param {string} sessionState - Current HeyGen session state (IDLE, CONNECTING, CONNECTED, DISCONNECTED)
 * @returns {Object} - { wsControlEnabled }
 */
export function useWebSocketControl(sessionInfo, aiFormData, repeatRef, sessionRef, isAvatarTalking, setDefaultLang, sessionState) {
  const socketRef = useRef(null);
  const [wsControlEnabled, setWsControlEnabled] = useState(false);
  const currentSpeechIDRef = useRef(null);
  const currentLanguageRef = useRef(localStorage.getItem('default_lang') || 'en');
  const sessionInfoRef = useRef(sessionInfo); // Track sessionInfo in a ref to avoid closure issues

  // Update sessionInfoRef whenever sessionInfo changes
  useEffect(() => {
    sessionInfoRef.current = sessionInfo;
    console.log('[WebSocket Control] sessionInfo updated:', sessionInfo);
  }, [sessionInfo]);

  useEffect(() => {
    // Log initialization attempt
    console.log('[WebSocket Control] useEffect triggered', {
      hasSessionInfo: !!sessionInfo,
      hasSessionId: !!sessionInfo?.session_id,
      hasAiFormData: !!aiFormData,
      sessionId: sessionInfo?.session_id
    });
    
    // Only initialize if we have valid aiFormData
    // Note: We don't require sessionInfo because WebSocket control should be ready
    // even before the avatar session starts
    if (!aiFormData) {
      console.warn('[WebSocket Control] Missing aiFormData, skipping initialization');
      return;
    }

    // Determine backend URL
    // In development: use localhost:5000
    // In production: use same origin (backend and frontend served from same domain)
    const backendUrl = process.env.NODE_ENV === 'production' 
      ? window.location.origin 
      : 'http://localhost:5000';

    console.log('[WebSocket Control] Initializing Socket.IO connection to:', backendUrl);

    // Initialize Socket.IO connection to backend
    const socket = io(backendUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    socketRef.current = socket;

    // Connection event handlers
    socket.on('connect', () => {
      console.log('✅ [WebSocket Control] Connected to backend Socket.IO');
      console.log('   Socket ID:', socket.id);
      setWsControlEnabled(true);
      
      // Emit frontend_connected event
      socket.emit('frontend_connected', {
        timestamp: new Date().toISOString(),
      });
      console.log('   Emitted frontend_connected event');
      
      // Test: Listen for ANY event to verify Socket.IO is working
      socket.onAny((eventName, ...args) => {
        console.log(`🔔 [Socket.IO] Received event: ${eventName}`, args);
      });
    });

    socket.on('disconnect', () => {
      console.log('⚠️ [WebSocket Control] Disconnected from backend Socket.IO');
      setWsControlEnabled(false);
    });

    socket.on('connect_error', (error) => {
      console.error('❌ [WebSocket Control] Connection error:', error.message);
      setWsControlEnabled(false);
    });

    // Listen for speech_command events from backend
    socket.on('speech_command', async (data) => {
      console.log('📨 [WebSocket Control] Received speech_command:', data);
      console.log('   speechID:', data.speechID);
      console.log('   text:', data.text);
      console.log('   language:', data.language);
      console.log('   Current state - sessionRef:', !!sessionRef?.current, 'repeatRef:', !!repeatRef?.current);
      
      const { speechID, text, language } = data;
      
      if (!text) {
        console.error('❌ [WebSocket Control] No text provided in speech_command');
        return;
      }

      // Store the current speechID for event correlation
      currentSpeechIDRef.current = speechID;
      console.log('✅ Set currentSpeechID:', speechID);

      try {
        // Update language if provided and different from current
        if (language) {
          if (language !== currentLanguageRef.current) {
            localStorage.setItem('default_lang', language);
            currentLanguageRef.current = language;
            
            // Update parent component's language state if setter is provided
            if (setDefaultLang && typeof setDefaultLang === 'function') {
              setDefaultLang(language);
            }
            
            console.log(`WebSocket Control: Language updated to ${language} for speech command`);
          }
        }

        // Call the existing repeat() function to trigger avatar speech
        // The repeat function expects: (session_id, text, action, disable, silence)
        // The repeat function will use the language from localStorage (default_lang)
        
        // Use refs to get the latest values (avoid closure issues)
        const currentSessionInfo = sessionInfoRef.current;
        
        // Check what's available
        const hasSessionRef = !!sessionRef?.current;
        const hasSessionId = !!currentSessionInfo?.session_id;
        const hasRepeatRef = !!repeatRef?.current;
        
        console.log('📊 Session readiness check:', {
          hasSessionRef,
          hasSessionId,
          hasRepeatRef,
          sessionInfo: currentSessionInfo
        });
        
        if (sessionRef?.current && currentSessionInfo?.session_id && repeatRef?.current) {
          console.log('✅ Session ready, calling repeat()');
          
          // Call repeat() to trigger avatar speech
          await repeatRef.current(currentSessionInfo.session_id, text, false, false, false);
          
          // CRITICAL FIX: Emit avatar_speaking_started immediately after calling repeat()
          // The HeyGen SDK's AVATAR_SPEAK_STARTED event is unreliable, so we emit it manually
          console.log('📤 Emitting avatar_speaking_started immediately (SDK event unreliable)');
          socket.emit('avatar_speaking_started', {
            speechID: speechID,
            timestamp: new Date().toISOString(),
          });
          
          // Fallback: If HeyGen SDK doesn't fire AVATAR_SPEAK_ENDED, emit it manually after estimated duration
          // This ensures events always fire even if SDK is unreliable
          const estimatedDuration = Math.max(text.length * 50, 2000); // 50ms per character, min 2s
          
          const fallbackTimer = setTimeout(() => {
            if (currentSpeechIDRef.current === speechID) {
              console.warn('⚠️ HeyGen SDK did not fire AVATAR_SPEAK_ENDED, emitting manually');
              socket.emit('avatar_speaking_ended', {
                speechID: speechID,
                timestamp: new Date().toISOString(),
                fallback: true
              });
              currentSpeechIDRef.current = null;
            }
          }, estimatedDuration + 2000); // Add 2 second buffer
          
          // Store timer reference to clear it if SDK fires event normally
          if (!window.speechFallbackTimers) {
            window.speechFallbackTimers = new Map();
          }
          window.speechFallbackTimers.set(speechID, fallbackTimer);
          
        } else {
          // Detailed error message
          let missingComponents = [];
          if (!sessionRef?.current) missingComponents.push('sessionRef');
          if (!currentSessionInfo?.session_id) missingComponents.push('sessionInfo.session_id');
          if (!repeatRef?.current) missingComponents.push('repeatRef');
          
          const errorMessage = `No active session or repeat function available. Missing: ${missingComponents.join(', ')}`;
          console.error('❌ [WebSocket Control]', errorMessage);
          console.error('   sessionInfo from ref:', currentSessionInfo);
          console.error('   sessionRef.current:', sessionRef?.current);
          console.error('   repeatRef.current:', !!repeatRef?.current);
          console.error('   Please start the avatar session first by clicking the start button in the UI');
          
          // Emit error back to backend
          socket.emit('avatar_speaking_error', {
            speechID,
            error: {
              code: 'SESSION_NOT_READY',
              message: errorMessage,
              missing: missingComponents
            },
            timestamp: new Date().toISOString(),
          });
        }
      } catch (error) {
        console.error('WebSocket Control: Error executing speech command', error);
        
        // Emit error back to backend
        socket.emit('avatar_speaking_error', {
          speechID,
          error: {
            code: 'SPEECH_FAILED',
            message: error.message || 'Unknown error'
          },
          timestamp: new Date().toISOString(),
        });
      }
    });

    // Listen for language_change events from backend
    socket.on('language_change', (data) => {
      console.log('WebSocket Control: Received language_change', data);
      
      const { language } = data;
      
      if (!language) {
        console.error('WebSocket Control: No language provided in language_change');
        return;
      }

      // Update the language setting in localStorage
      localStorage.setItem('default_lang', language);
      
      // Update the current language reference for subsequent speech commands
      currentLanguageRef.current = language;
      
      // Update the parent component's language state if setter is provided
      if (setDefaultLang && typeof setDefaultLang === 'function') {
        setDefaultLang(language);
      }
      
      console.log(`WebSocket Control: Language changed to ${language}`);
    });

    // Listen for stop_speech events from backend
    socket.on('stop_speech', (data) => {
      console.log('WebSocket Control: Received stop_speech', data);
      
      const { speechID } = data;
      
      try {
        // Stop the avatar from speaking using the HeyGen SDK
        if (sessionRef?.current) {
          // Call the interrupt method to stop current speech
          if (typeof sessionRef.current.interrupt === 'function') {
            sessionRef.current.interrupt();
            console.log('WebSocket Control: Avatar speech interrupted');
          } else {
            console.warn('WebSocket Control: Session interrupt method not available');
          }
        } else {
          console.warn('WebSocket Control: No active session to stop');
        }

        // Clear the current speechID
        currentSpeechIDRef.current = null;

        // Emit confirmation back to backend
        socket.emit('avatar_speaking_ended', {
          speechID: speechID || currentSpeechIDRef.current,
          stopped: true,
          timestamp: new Date().toISOString(),
        });
      } catch (error) {
        console.error('WebSocket Control: Error stopping speech', error);
        
        // Emit error back to backend
        socket.emit('avatar_speaking_error', {
          speechID: speechID || currentSpeechIDRef.current,
          error: {
            code: 'STOP_FAILED',
            message: error.message || 'Failed to stop speech'
          },
          timestamp: new Date().toISOString(),
        });
      }
    });

    // Cleanup on unmount
    return () => {
      if (socket) {
        // Emit frontend_disconnected event
        socket.emit('frontend_disconnected', {
          timestamp: new Date().toISOString(),
        });
        
        socket.disconnect();
      }
    };
  }, [aiFormData, repeatRef, sessionRef]); // Removed sessionInfo from dependencies since we don't need it to initialize

  // Wire HeyGen SDK events to WebSocket events
  // Listen directly to AVATAR_SPEAK_STARTED and AVATAR_SPEAK_ENDED events from the HeyGen SDK
  useEffect(() => {
    const session = sessionRef?.current;
    const socket = socketRef.current;

    if (!session || !socket) {
      return;
    }

    // Handler for AVATAR_SPEAK_STARTED event
    const handleAvatarSpeakStarted = () => {
      console.log('WebSocket Control: Avatar started speaking (SDK event)');
      console.log('Current speechID:', currentSpeechIDRef.current);
      
      // Only emit if we have a current speechID
      if (currentSpeechIDRef.current) {
        socket.emit('avatar_speaking_started', {
          speechID: currentSpeechIDRef.current,
          timestamp: new Date().toISOString(),
        });
        console.log('✅ Emitted avatar_speaking_started for speechID:', currentSpeechIDRef.current);
      } else {
        console.warn('⚠️ AVATAR_SPEAK_STARTED fired but no speechID set!');
      }
    };

    // Handler for AVATAR_SPEAK_ENDED event
    const handleAvatarSpeakEnded = () => {
      console.log('WebSocket Control: Avatar stopped speaking (SDK event)');
      console.log('Current speechID:', currentSpeechIDRef.current);
      
      // Only emit if we have a current speechID
      if (currentSpeechIDRef.current) {
        const speechID = currentSpeechIDRef.current; // Store before clearing
        
        // Clear fallback timer if it exists
        if (window.speechFallbackTimers && window.speechFallbackTimers.has(speechID)) {
          clearTimeout(window.speechFallbackTimers.get(speechID));
          window.speechFallbackTimers.delete(speechID);
          console.log('✅ Cleared fallback timer for speechID:', speechID);
        }
        
        socket.emit('avatar_speaking_ended', {
          speechID: speechID,
          timestamp: new Date().toISOString(),
        });
        
        console.log('✅ Emitted avatar_speaking_ended for speechID:', speechID);

        // Clear the current speechID after speaking ends
        currentSpeechIDRef.current = null;
      } else {
        console.warn('⚠️ AVATAR_SPEAK_ENDED fired but no speechID set!');
      }
    };

    // Handler for session errors that might affect speech
    const handleSessionError = (error) => {
      console.error('WebSocket Control: Session error detected', error);
      
      // If we have a current speech in progress, emit error event
      if (currentSpeechIDRef.current) {
        socket.emit('avatar_speaking_error', {
          speechID: currentSpeechIDRef.current,
          error: {
            code: 'SESSION_ERROR',
            message: error?.message || 'Session error occurred during speech'
          },
          timestamp: new Date().toISOString(),
        });

        // Clear the current speechID after error
        currentSpeechIDRef.current = null;
      }
    };

    // Register event listeners on the HeyGen SDK session
    session.on(AgentEventsEnum.AVATAR_SPEAK_STARTED, handleAvatarSpeakStarted);
    session.on(AgentEventsEnum.AVATAR_SPEAK_ENDED, handleAvatarSpeakEnded);
    
    // Listen for session errors (if the SDK provides error events)
    // Note: The HeyGen SDK may emit errors through various mechanisms
    // We'll catch them through the session's error event if available
    if (typeof session.on === 'function') {
      try {
        session.on('error', handleSessionError);
      } catch (e) {
        // Error event may not be available in this SDK version
        console.log('WebSocket Control: Error event listener not available on session');
      }
    }

    // Cleanup: Remove event listeners when dependencies change or component unmounts
    return () => {
      session.off(AgentEventsEnum.AVATAR_SPEAK_STARTED, handleAvatarSpeakStarted);
      session.off(AgentEventsEnum.AVATAR_SPEAK_ENDED, handleAvatarSpeakEnded);
      
      // Remove error listener if it was added
      try {
        session.off('error', handleSessionError);
      } catch (e) {
        // Ignore if error event was not available
      }
    };
  }, [sessionRef]);

  // Notify backend when session state changes
  // This keeps the Control Bridge in sync with the frontend session state
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !sessionState) {
      return;
    }

    console.log('WebSocket Control: Session state changed to', sessionState);

    // Map HeyGen SessionState to Control Bridge state
    let bridgeState = 'INACTIVE';
    
    if (sessionState === SessionState.IDLE) {
      bridgeState = 'INACTIVE';
    } else if (sessionState === SessionState.CONNECTING) {
      bridgeState = 'CONNECTING';
    } else if (sessionState === SessionState.CONNECTED) {
      bridgeState = 'CONNECTED';
    } else if (sessionState === SessionState.DISCONNECTED) {
      bridgeState = 'DISCONNECTED';
    }

    // Emit session state change to backend
    socket.emit('session_state_changed', {
      state: bridgeState,
      timestamp: new Date().toISOString(),
    });

    console.log('WebSocket Control: Notified backend of session state:', bridgeState);
  }, [sessionState]);

  return {
    wsControlEnabled,
    emitStateChange: (state) => {
      if (socketRef.current && socketRef.current.connected) {
        socketRef.current.emit('session_state_changed', {
          state: state,
          timestamp: new Date().toISOString()
        });
        console.log(`✅ WebSocket Control: Emitted state change to backend: ${state}`);
      } else {
        console.warn('⚠️ WebSocket Control: Cannot emit state change - socket not connected');
      }
    },
    emitVideoMode: (mode) => {
      if (socketRef.current && socketRef.current.connected) {
        socketRef.current.emit('video_mode_changed', {
          mode: mode,
          timestamp: new Date().toISOString()
        });
        console.log(`✅ WebSocket Control: Emitted video mode to backend: ${mode}`);
      } else {
        console.warn('⚠️ WebSocket Control: Cannot emit video mode - socket not connected');
      }
    }
  };
}
